<script setup lang="ts">
import { computed, watch, onMounted } from 'vue';
import { IonButton, IonContent, IonIcon, IonSpinner, IonItem, IonList } from '@ionic/vue';
import { arrowForwardOutline, checkmarkCircle, close } from 'ionicons/icons';
import { TransactionType, TransactionSubtype } from '@/enums/wallet.enum';
import { useI18n } from 'vue-i18n';
import router from '@/router';

import ItemsDetails from '@/shared/components/ui/ItemsDetails.vue';
import TopUpItemsSummary from '@/shared/components/ui/TopUpItemsSummary.vue';
import TopAlert from '@/shared/components/ui/TopAlert.vue';
import useTenant from '@/shared/composables/useTenant';
import useUtils from '@/shared/composables/useUtils';
import useTransaction from '@/shared/composables/useTransaction';
import useUserProfile from '@/modules/profile/composables/useUserProfile';
import { useSharedStore } from '@/stores/shared/sharedStore';
import useTopUp from '@/modules/top-up/composables/useTopUp';
import { useAuthStore } from '@/stores/auth/authStore';

const { t } = useI18n();
const { getTenantConfigAndStyles } = useTenant();
const { tenantConfig } = getTenantConfigAndStyles();
const { transaction, transactionStatus, goTo } = useTransaction();
const { replaceTopUpGeneralMessageVariables } = useUtils();
const { getUserProfile } = useUserProfile();
const { userProfile } = getUserProfile();
const sharedStore = useSharedStore();
const authStore = useAuthStore();

onMounted(() => {
	// Redirect to dashboard if no items are present
	// This may happen if user was already on this page
	// and clicked continue
	// Because that would clean the transaction items
	if (!transaction || (transaction && transaction.items && transaction.items.length === 0)) {
		router.push('/dashboard');
	}
});

// Country setting fix (don't touch)
const { persistDefaultCountryIfNoneWasSet } = useTopUp(tenantConfig, undefined as any);
watch(transactionStatus, (value) => {
	if (value.success) {
		persistDefaultCountryIfNoneWasSet();
	}
});

const sharedClaimSuccessMessage = computed(() => {
	const additionalInfo = transaction.additionalInfo;

	if (!additionalInfo || typeof additionalInfo !== 'object') {
		return '';
	}

	const message = additionalInfo.sharedClaimSuccessMessage;

	return typeof message === 'string' && message.trim() ? message : '';
});

const topUpConfirmationMessageTitle = computed(() => {
	if (sharedClaimSuccessMessage.value) {
		return '';
	}

	return tenantConfig.value?.top_up_confirmation_message_title || '';
});

const topUpConfirmationMessageBody = computed(() => {
	return sharedClaimSuccessMessage.value || tenantConfig.value?.top_up_confirmation_message_body || '';
});
</script>

<template>
	<ion-content>
		<div v-if="!transactionStatus || (!transactionStatus.finished && !transactionStatus.isPolling)" class="flex w-full h-full items-center justify-center">
			<ion-spinner name="dots"></ion-spinner>
		</div>

		<!-- Payment in progress state -->
		<div v-if="transactionStatus && transactionStatus.isPolling" class="flex w-full h-full items-center justify-center px-[34px]">
			<div class="flex flex-col items-center justify-center text-center p-8 bg-white rounded-xl shadow-sm w-full max-w-sm">
				<ion-spinner name="dots" class="mb-4 text-dark-blue"></ion-spinner>
				<h2 class="font-sourcesanspro-semibold text-lg mb-2 text-dark-blue">
					{{ transactionStatus.pollingMessage }}
				</h2>
				<p class="font-sourcesanspro-regular text-base text-dark-blue/70 mb-6">
					{{ t('transaction.checking_payment_status') }}
				</p>
				<ion-button class="w-full hover:bg-white hover:rounded-full mt-3" expand="full" size="large" shape="round" fill="outline" @click="router.push('/dashboard')">
					{{ t('general.go_back') }}
				</ion-button>
			</div>
		</div>

		<div v-if="transactionStatus && transactionStatus.finished" class="flex w-full" :class="[{ 'h-full': transactionStatus.success }]">
			<!-- SUCCESS -->
			<div v-if="transactionStatus.success" class="flex flex-col w-full h-full justify-between">
				<TopAlert
					class="mb-2 pt-[75px] pb-12"
					:class="[
						{
							'bg-anykrowd-green': !tenantConfig?.viva_wallet_payconiq_preferred_mode || (tenantConfig?.viva_wallet_payconiq_preferred_mode && !sharedStore.shouldUsePayconiq),
							'payconiq': tenantConfig?.viva_wallet_payconiq_preferred_mode && sharedStore.shouldUsePayconiq,
						},
					]">
					<div class="flex flex-col items-center justify-center">
						<ion-icon :icon="checkmarkCircle" class="text-white text-[78px] mb-2" />
						<h1 v-if="transaction.subtype !== TransactionSubtype.PACKAGE" class="font-sourcesanspro-semibold text-[40px] mb-4">
							{{ transaction.title ? transaction.title : transaction.totalPriceString }}
						</h1>
						<span class="font-sourcesanspro-regular text-m2">
							{{ transaction.subtitle ? transaction.subtitle : t(transactionStatus.subTitle) }}
						</span>
					</div>
				</TopAlert>

				<!-- Message box -->
				<div v-if="transaction.type != TransactionType.WALLET_MERGE">
					<div class="flex flex-col mx-[34px] p-4 mb-2 bg-white/10 rounded-lg">
						<div class="font-sourcesanspro-semibold text-m2" v-if="topUpConfirmationMessageTitle">
							{{ t(topUpConfirmationMessageTitle) }}
						</div>
						<div v-if="userProfile && topUpConfirmationMessageBody" v-dompurify-html="replaceTopUpGeneralMessageVariables(topUpConfirmationMessageBody, userProfile)"></div>
					</div>
				</div>
				<div class="flex flex-col w-full px-[34px] align-center mb-[34px]">
					<TopUpItemsSummary
						v-if="transaction.type === TransactionType.TOP_UP"
						:items="transaction.items"
						:total-price="transaction.totalPriceNumber"
						:wallet-receives="transaction.subtype === TransactionSubtype.PACKAGE ? undefined : transaction.totalPriceWithoutFee"
						variant="light"
						class="mb-4"
						:title="t('transaction.order_details')"
						:total-title="transaction.totalDescription"
					/>

					<ItemsDetails
						v-if="transaction.type !== TransactionType.WALLET_MERGE && transaction.type !== TransactionType.TOP_UP"
						border-class="border-y border-y-slate-200"
						class="mb-4 bg-white rounded-xl"
						text-class="text-dark-blue"
						text-class-two="text-dark-blue/70"
						:title="t('transaction.order_details')"
						:items="
							transaction.items.map((item) => {
								return {
									itemId: item.itemId,
									title: item.description,
									price: item.unitPrice,
									quantity: item.quantity,
									hideQuantity: item.hideQuantity,
									hidePrice: item.hidePrice,
									totalPrice: item.itemPrice,
									discount: item.discount,
									currency: item.currency,
								};
							})
						"
						:total-title="transaction.totalDescription"
						:total-price="transaction.totalPriceNumber"
						:total-price-string="transaction.type === TransactionType.SELF_ORDER ? transaction.totalPriceString : transaction.totalPriceString || '0'"
						:show-total-price-string="transaction.showTotalPriceString" />

					<div v-if="transaction.type === TransactionType.WALLET_MERGE">
						<ion-list class="mb-4 bg-white rounded-xl">
							<div class="flex px-6 py-4 items-center text-dark-blue">
								<span class="font-sourcesanspro-semibold text-l2">{{ t('transaction.wallet_merged') }}</span>
							</div>
							<div class="border-t border-t-slate-200">
								<ion-item class="font-sourcesanspro-regular ion-no-padding text-dark-blue/70" lines="none">
									<div class="flex px-6 py-2 w-full justify-between">
										<div>
											<span class="text-base">{{ transaction.items[0].description }}</span>
										</div>
									</div>
								</ion-item>
							</div>
						</ion-list>
					</div>

					<ion-button class="w-full hover:bg-white hover:rounded-full" expand="full" size="large" shape="round" @click="goTo">
						<div class="flex w-full items-center justify-between">
							{{ authStore.isAuthenticated() ? t('transaction.continue') : t('transaction.order_again') }}
							<ion-icon :icon="arrowForwardOutline" class="text-dark-blue mr-2"></ion-icon>
						</div>
					</ion-button>
				</div>
			</div>

			<TopAlert v-if="!transactionStatus.success" class="mb-8 pt-[48px] pb-12 bg-anykrowd-red">
				<div class="flex items-center justify-start cursor-pointer" @click="goTo">
					<ion-icon :icon="close" class="text-xl text-white"></ion-icon>
				</div>
				<div class="flex flex-col items-center justify-center">
					<h2 class="mb-4 font-sourcesanspro-semibold text-lg">
						{{ t(transactionStatus.errorTitle) }}
					</h2>
					<span class="w-[337px] text-center font-sourcesanspro-regular text-base2 leading-[26px]">
						{{ t(transactionStatus.errorSubTitle) }}
					</span>
				</div>
			</TopAlert>
		</div>
	</ion-content>
</template>

<style lang="scss" scoped>
ion-item {
	--background: transparent;
	--inner-padding-end: 0;
	--min-height: 30px;
}

.payconiq {
	background-color: #ff4785;
}
</style>
